import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { LoadingNote } from '@/components/OfflineDetails';
import { useState } from 'react';
import { api } from '@/api/client';
import type { components } from '@/api/schema';
import { ActionBar, DataGrid, type DesktopAction, type GridColumn } from '@/components/desktop';
import {
  ErrorNote,
  Field,
  FormActions,
  FormPanel,
  Modal,
  OkNote,
  SearchBar,
  Table,
  TextArea,
  Tile,
  errorMessage,
  shortDate,
} from '@/components/ui';
import { AppIcon } from '@/components/AppIcon';
import { StorePicker } from '@/components/StorePicker';
import { useCodePreview, useStores, type StorageBinRow } from '@/hooks/inventory';
import {
  BIN_CODE,
  MAX_BINS_AT_ONCE,
  expandRange,
  splitClashes,
  type Clash,
  type NewBin,
} from './binRange';
import { useAuth } from '@/stores/auth';
import { DeletedNote, RecordPage, type Notice } from './referencePage';
import { useOpenRecord } from '@/hooks/useOpenRecord';

type StoreRow = components['schemas']['StoreDto'];
type RetireResult = components['schemas']['RetireResultDto'];

/** Bins are optional; every empty state says so, so nobody thinks they must set them up. */
const OPTIONAL_NOTE =
  'Bins are optional. Add them only to track where in a store stock is kept — stock received without a bin sits in the store itself.';

/** A bin's own screen. */
function BinPage({ row }: { row: StorageBinRow }) {
  return (
    <>
      {!row.isActive && <DeletedNote what="bin" />}
      <div className="tiles">
        <Tile label="Store" value={row.storeCode} />
        <Tile label="Items in it now" value={String(row.itemsInStock)} />
        <Tile label="Last moved" value={row.lastMovementAt ? shortDate(row.lastMovementAt) : '—'} />
      </div>
      <section className="stack docpage__section">
        <h3 className="formsection">Bin</h3>
        <BinDetails row={row} />
      </section>
    </>
  );
}

function BinDetails({ row }: { row: StorageBinRow }) {
  return (
    <dl className="detaillist">
      <dt>Code</dt>
      <dd className="mono">{row.code}</dd>
      <dt>Name</dt>
      <dd>
        {row.nameEn}
        {row.nameUr && <span className="urdu"> · {row.nameUr}</span>}
      </dd>
      <dt>Store</dt>
      <dd>
        {row.storeCode} — {row.storeName}
      </dd>
      <dt>Farm</dt>
      <dd>{row.farmName ? <>{row.farmCode} — {row.farmName}</> : '—'}</dd>
      <dt>Items in it now</dt>
      <dd>{row.itemsInStock}</dd>
      <dt>Last moved</dt>
      <dd>{row.lastMovementAt ? shortDate(row.lastMovementAt) : '—'}</dd>
      <dt>Notes</dt>
      <dd>{row.notes ?? '—'}</dd>
      <dt>Status</dt>
      <dd>{row.isActive ? <span className="pill">Active</span> : <span className="pill pill--alert">⚠ Deleted</span>}</dd>
    </dl>
  );
}

/** Clashing codes shown before the rest fold behind "+N more". */
const CLASHES_SHOWN = 12;

function plural(n: number, one: string, many = one + 's'): string {
  return `${n} ${n === 1 ? one : many}`;
}

/** What "Add N bins" will do: how many are new, which already exist, and a sample. */
function RangePreview({
  fresh,
  clashing,
  storeCode,
}: {
  fresh: NewBin[];
  clashing: Clash[];
  storeCode: string;
}) {
  const [showAll, setShowAll] = useState(false);
  const total = fresh.length + clashing.length;
  const sample =
    fresh.length <= 5 ? fresh : [...fresh.slice(0, 3), null, fresh[fresh.length - 1]!];
  const shownClashes = showAll ? clashing : clashing.slice(0, CLASHES_SHOWN);

  return (
    <div className="binpreview formgrid__full" aria-live="polite">
      <p className="binpreview__summary">
        {clashing.length === 0 ? (
          <>
            <strong>{plural(total, 'bin')}</strong>
            {total > 1 && (
              <span className="mono"> · {fresh[0]!.code} → {fresh[fresh.length - 1]!.code}</span>
            )}
          </>
        ) : (
          <>
            <strong>{fresh.length} new</strong>
            {' · '}
            <span className="pill pill--warn">
              {clashing.length} already in {storeCode}
            </span>
            <span className="dim">
              {fresh.length === 0
                ? ' Every bin in this range is already there.'
                : ` ${clashing.length === 1 ? 'It' : 'They'} will be left as ${clashing.length === 1 ? 'it is' : 'they are'}.`}
            </span>
          </>
        )}
      </p>

      {clashing.length > 0 && (
        <div className="binpreview__group">
          <span className="field__label">Already in {storeCode}</span>
          <ul className="binpreview__chips">
            {shownClashes.map((c) => (
              <li
                key={c.bin.code}
                className={`pill ${c.existing.isActive ? 'pill--warn' : 'pill--alert'}`}
                title={
                  c.by === 'code'
                    ? `${c.existing.code} is "${c.existing.nameEn}"${c.existing.isActive ? '' : ' (deleted — reactivate it instead)'}`
                    : `The name "${c.bin.nameEn}" is already used by ${c.existing.code}`
                }
              >
                <span className="mono">{c.bin.code}</span>
                {c.by === 'name' && ` · name "${c.bin.nameEn}" taken by ${c.existing.code}`}
                {c.by === 'code' && !c.existing.isActive && ' · deleted'}
              </li>
            ))}
            {clashing.length > CLASHES_SHOWN && (
              <li>
                <button type="button" className="btn btn--ghost btn--sm" onClick={() => setShowAll((v) => !v)}>
                  {showAll ? 'Show fewer' : `+${clashing.length - CLASHES_SHOWN} more`}
                </button>
              </li>
            )}
          </ul>
        </div>
      )}

      {fresh.length > 0 && (
        <div className="binpreview__group">
          <span className="field__label">Will be added</span>
          <ul className="binpreview__sample">
            {sample.map((b, i) =>
              b ? (
                <li key={b.code}>
                  <span className="mono">{b.code}</span> <span className="dim">{b.nameEn}</span>
                </li>
              ) : (
                <li key={`gap-${i}`} className="dim" aria-label={`and ${fresh.length - 4} more`}>
                  … {fresh.length - 4} more …
                </li>
              ),
            )}
          </ul>
        </div>
      )}
    </div>
  );
}

function BinForm({
  initial,
  storeId: fixedStoreId,
  onCancel,
  onSaved,
}: {
  initial?: StorageBinRow | undefined;
  /** Pre-chooses the store, as the Bins tab of a store does. */
  storeId?: string | undefined;
  onCancel: () => void;
  onSaved: (message: string) => void;
}) {
  const editing = initial !== undefined;
  const [mode, setMode] = useState<'one' | 'many'>('one');
  const [form, setForm] = useState({
    storeId: initial?.storeId ?? fixedStoreId ?? '',
    /** What follows the fixed prefix; null while an edited bin's code is untouched. */
    code: initial ? (null as string | null) : '',
    nameEn: initial?.nameEn ?? '',
    nameUr: initial?.nameUr ?? '',
    notes: initial?.notes ?? '',
  });
  const [range, setRange] = useState({ from: '1', to: '', namePrefix: '' });
  const [problem, setProblem] = useState<string | null>(null);
  const set = (patch: Partial<typeof form>) => { setForm((f) => ({ ...f, ...patch })); setProblem(null); };
  const setR = (patch: Partial<typeof range>) => { setRange((r) => ({ ...r, ...patch })); setProblem(null); };

  const queryClient = useQueryClient();
  const { data: stores } = useStores();
  // Also gives the fixed start of every bin code, set in Settings (e.g. "BIN").
  const nextCode = useCodePreview('StorageBin', true);
  const prefix = nextCode.data?.prefix ?? '';
  // Deleted bins too: their codes and names are still taken.
  const storeBinsKey = ['storage-bins', 'form', form.storeId];
  const { data: storeBins } = useQuery({
    queryKey: storeBinsKey,
    queryFn: () => api.get('/api/v1/storage-bins', { storeId: form.storeId, includeInactive: 'true' }),
    enabled: !!form.storeId,
  });
  const others = (storeBins ?? []).filter((b) => b.id !== initial?.id);
  const storeCode = stores?.find((s: StoreRow) => s.id === form.storeId)?.code ?? 'This store';

  // One bin: the code is the fixed prefix plus what was typed after it. A bin
  // made before codes had a fixed start keeps its code as it is.
  const legacyCode =
    !!initial && !!nextCode.data && !initial.code.toLowerCase().startsWith(prefix.toLowerCase());
  const suffix = (form.code ?? (initial ? initial.code.slice(prefix.length) : '')).trim();
  const typedCode = legacyCode ? initial.code : suffix ? prefix + suffix : '';
  const codeTaken = typedCode ? others.find((b) => b.code.toLowerCase() === typedCode.toLowerCase()) : undefined;
  const nameTaken = form.nameEn.trim()
    ? others.find((b) => b.nameEn.toLowerCase() === form.nameEn.trim().toLowerCase())
    : undefined;
  const codeBad = typedCode !== '' && !BIN_CODE.test(typedCode);
  // Checked as they type, against the store's bins once they have loaded.
  const codeFree =
    !legacyCode && !!suffix && !!storeBins && !codeBad && !codeTaken && typedCode !== initial?.code;
  const takenHint = (b: StorageBinRow, what: string) =>
    b.isActive
      ? `${storeCode} already has ${what}: ${b.code} — ${b.nameEn}`
      : `${b.code} — ${b.nameEn} is a deleted bin in ${storeCode}. Reactivate it instead.`;

  // Many bins: the range, and which of it the store already has.
  const expanded = expandRange({ ...range, prefix });
  const { fresh, clashing } = splitClashes(expanded.bins, others);

  const save = useMutation({
    mutationFn: async (): Promise<string> => {
      if (initial) {
        await api.patch('/api/v1/storage-bins/' + initial.id, {
          ...(!legacyCode && typedCode !== initial.code ? { code: typedCode } : {}),
          nameEn: form.nameEn.trim(),
          nameUr: form.nameUr.trim(),
          notes: form.notes.trim(),
        });
        return `${typedCode} updated.`;
      }
      if (mode === 'many') {
        const result = await api.post<{ created: number; codes: string[] }>('/api/v1/storage-bins/bulk', {
          storeId: form.storeId,
          bins: fresh,
          ...(form.notes.trim() ? { notes: form.notes.trim() } : {}),
        });
        const span =
          result.created === 1 ? result.codes[0] : `${result.codes[0]} → ${result.codes[result.codes.length - 1]}`;
        return (
          `Added ${plural(result.created, 'bin')} to ${storeCode} (${span}).` +
          (clashing.length === 1
            ? ' 1 was already there and left as it is.'
            : clashing.length > 1
              ? ` ${clashing.length} were already there and left as they are.`
              : '')
        );
      }
      const result = await api.post<{ id: string; code: string }>('/api/v1/storage-bins', {
        storeId: form.storeId,
        ...(typedCode ? { code: typedCode } : {}),
        nameEn: form.nameEn.trim(),
        ...(form.nameUr.trim() ? { nameUr: form.nameUr.trim() } : {}),
        ...(form.notes.trim() ? { notes: form.notes.trim() } : {}),
      });
      return `${result.code} added.`;
    },
    onSuccess: onSaved,
    // Someone may have added bins meanwhile: refresh what the form checks against.
    onError: () => void queryClient.invalidateQueries({ queryKey: storeBinsKey }),
  });

  function submit() {
    if (!form.storeId) return setProblem('Choose the store this bin is in.');
    if (!nextCode.data && (mode === 'many' || suffix)) {
      return setProblem('Still loading the bin code prefix from Settings. Try again in a moment.');
    }
    if (mode === 'many' && !editing) {
      if (!range.from.trim() || !range.to.trim()) return setProblem('Fill in From and To, such as 1 and 100.');
      if (expanded.problem) return setProblem(expanded.problem);
      if (fresh.length === 0) return setProblem(`Every bin in this range is already in ${storeCode}.`);
    } else {
      if (editing && !typedCode) return setProblem('A bin needs a code.');
      if (codeBad) return setProblem(`After ${prefix}, a code can hold letters, digits, . _ / - and spaces, up to 24 characters in all.`);
      if (codeTaken) return setProblem(takenHint(codeTaken, 'that code'));
      if (!form.nameEn.trim()) return setProblem('Give the bin a name, such as "Rack A".');
      if (nameTaken) return setProblem(takenHint(nameTaken, 'that name'));
    }
    setProblem(null);
    save.mutate();
  }

  const storeOptions = stores?.filter((s: StoreRow) => s.isActive || s.id === form.storeId);

  const many = mode === 'many' && !editing;
  const submitLabel = editing
    ? 'Save changes'
    : many
      ? fresh.length > 0
        ? `Add ${plural(fresh.length, 'bin')}`
        : 'Add bins'
      : 'Add bin';

  return (
    <FormPanel
      title={editing ? `Edit ${initial.code}` : many ? 'Add storage bins' : 'Add a storage bin'}
      icon={<AppIcon kind="bin" size={18} />}
    >
      <form className="stack" onSubmit={(e) => { e.preventDefault(); submit(); }}>
        <div className="formgrid formgrid--3">
          <StorePicker
            label="Store"
            required
            span={editing ? 'full' : 2}
            value={form.storeId}
            onChange={(storeId) => set({ storeId })}
            disabled={editing || fixedStoreId !== undefined}
            hint={editing ? 'Cannot be changed once created' : undefined}
            stores={storeOptions}
          />
          {!editing && (
            <div className="field" role="group" aria-label="How many bins">
              <span className="field__label">Add</span>
              <span className="settings__choices">
                <button type="button" className={`chip ${!many ? 'chip--on' : ''}`} aria-pressed={!many} onClick={() => { setMode('one'); setProblem(null); }}>
                  One bin
                </button>
                <button type="button" className={`chip ${many ? 'chip--on' : ''}`} aria-pressed={many} onClick={() => { setMode('many'); setProblem(null); }}>
                  Many bins
                </button>
              </span>
            </div>
          )}

          {many ? (
            <>
              <Field
                label="From"
                required
                mono
                numeric
                prefix={prefix}
                value={range.from}
                onChange={(from) => setR({ from })}
                placeholder="1"
                maxLength={4}
                hint={`Type "001" for ${prefix}001, ${prefix}002…`}
              />
              <Field
                label="To"
                required
                mono
                numeric
                prefix={prefix}
                value={range.to}
                onChange={(to) => setR({ to })}
                placeholder="100"
                maxLength={4}
                hint={`Up to ${MAX_BINS_AT_ONCE} bins at a time`}
              />
              <Field
                label="Name prefix"
                value={range.namePrefix}
                onChange={(namePrefix) => setR({ namePrefix })}
                placeholder="e.g. Rack"
                maxLength={70}
                hint={
                  range.namePrefix.trim()
                    ? `Names: ${range.namePrefix.trim()} 1, ${range.namePrefix.trim()} 2…`
                    : 'Leave blank to name each bin after its code'
                }
              />
            </>
          ) : (
            <>
              {legacyCode ? (
                <Field
                  label="Code"
                  mono
                  value={initial.code}
                  onChange={() => {}}
                  disabled
                  hint={`Made before bin codes started with ${prefix}, so it stays as it is`}
                />
              ) : (
                <Field
                  label="Code"
                  mono
                  required={editing}
                  prefix={prefix}
                  value={form.code ?? suffix}
                  onChange={(code) => set({ code })}
                  maxLength={Math.max(1, 24 - prefix.length)}
                  invalid={!!codeTaken || codeBad}
                  hint={
                    codeTaken ? (
                      <span className="hint--alert">{takenHint(codeTaken, 'that code')}</span>
                    ) : codeBad ? (
                      <span className="hint--alert">Letters, digits, . _ / - and spaces only</span>
                    ) : codeFree ? (
                      <span className="hint--ok">✓ {typedCode} is free in {storeCode}</span>
                    ) : editing ? (
                      'Unique within this store'
                    ) : (
                      'Leave blank to number it automatically'
                    )
                  }
                />
              )}
              <Field
                label="Name"
                required
                value={form.nameEn}
                onChange={(nameEn) => set({ nameEn })}
                placeholder="e.g. Rack A"
                invalid={!!nameTaken}
                hint={nameTaken ? <span className="hint--alert">{takenHint(nameTaken, 'that name')}</span> : undefined}
              />
              <Field label="Name (Urdu)" urdu value={form.nameUr} onChange={(nameUr) => set({ nameUr })} />
            </>
          )}

          <TextArea
            label="Notes"
            value={form.notes}
            onChange={(notes) => set({ notes })}
            rows={2}
            placeholder={many ? 'Where they are, what goes in them… (given to every bin)' : 'Where it is, what goes in it…'}
          />

          {many && !expanded.problem && expanded.bins.length > 0 && form.storeId && (
            <RangePreview fresh={fresh} clashing={clashing} storeCode={storeCode} />
          )}
        </div>

        {many && expanded.problem && <ErrorNote message={expanded.problem} />}
        {problem && problem !== expanded.problem && <ErrorNote message={problem} />}
        {save.isError && <ErrorNote message={errorMessage(save.error, 'Could not save')} />}

        <FormActions onCancel={onCancel} cancelLabel={editing ? 'Cancel' : 'Close'}>
          <button
            type="submit"
            className="btn btn--primary"
            disabled={save.isPending || (many && expanded.bins.length > 0 && fresh.length === 0)}
          >
            {save.isPending ? 'Saving…' : submitLabel}
          </button>
        </FormActions>
      </form>
    </FormPanel>
  );
}

/** Delete and reactivate, shared by the bins list and a store's Bins tab. */
function useBinLifecycle(
  setNotice: (n: { tone: 'ok' | 'error'; message: string }) => void,
  after: { retired?: (result: RetireResult) => void; reactivated?: () => void } = {},
) {
  const queryClient = useQueryClient();
  const invalidate = () => void queryClient.invalidateQueries({ queryKey: ['storage-bins'] });

  const retire = useMutation({
    mutationFn: (id: string) => api.delete<RetireResult>(`/api/v1/storage-bins/${id}`),
    onSuccess: (result) => { invalidate(); after.retired?.(result); setNotice({ tone: 'ok', message: result.message }); },
    onError: (e) => setNotice({ tone: 'error', message: errorMessage(e, 'Could not delete the bin') }),
  });
  const reactivate = useMutation({
    mutationFn: (id: string) => api.patch(`/api/v1/storage-bins/${id}`, { isActive: true }),
    onSuccess: () => { invalidate(); after.reactivated?.(); setNotice({ tone: 'ok', message: 'Bin reactivated.' }); },
    onError: (e) => setNotice({ tone: 'error', message: errorMessage(e, 'Could not reactivate the bin') }),
  });

  function confirmRetire(row: StorageBinRow) {
    if (
      window.confirm(
        `Delete ${row.code}? If it was never used, it is deleted. If it was used, it is switched off: it takes no new stock, and anything still in it can be issued as normal.`,
      )
    ) {
      retire.mutate(row.id);
    }
  }

  return { invalidate, confirmRetire, reactivate: (row: StorageBinRow) => reactivate.mutate(row.id) };
}

/** The Storage Bins list in the Items Manager. */
export function BinsPanel() {
  const { can } = useAuth();
  const [search, setSearch] = useState('');
  const [storeId, setStoreId] = useState('');
  const [showRetired, setShowRetired] = useState(false);
  const [selectedKey, setSelectedKey] = useState<string | null>(null);
  const [form, setForm] = useState<'create' | 'edit' | null>(null);
  const [notice, setNotice] = useState<Notice>(null);

  const { data: stores } = useStores();
  const { data, isPending, isError, error } = useQuery({
    queryKey: ['storage-bins', 'reference', storeId, showRetired],
    queryFn: () =>
      api.get('/api/v1/storage-bins', {
        storeId: storeId || undefined,
        includeInactive: showRetired ? 'true' : undefined,
      }),
  });
  const { invalidate, confirmRetire, reactivate } = useBinLifecycle(setNotice, {
    retired: (result) => afterRetire(result),
    reactivated: () => page.patch({ isActive: true }),
  });

  const all = data ?? [];
  const needle = search.trim().toLowerCase();
  const rows = needle
    ? all.filter(
        (b) =>
          b.code.toLowerCase().includes(needle) ||
          b.nameEn.toLowerCase().includes(needle) ||
          b.storeName.toLowerCase().includes(needle),
      )
    : all;
  const page = useOpenRecord<StorageBinRow>(rows);
  const selected = rows.find((r) => r.id === selectedKey) ?? (page.row?.id === selectedKey ? page.row : null);

  function show(row: StorageBinRow | null) {
    if (row) {
      setSelectedKey(row.id);
      page.open(row);
    } else page.close();
    setNotice(null);
  }

  /** Deleted outright: nothing left to show. Switched off: still there, deleted. */
  function afterRetire(result: RetireResult) {
    if (result.outcome === 'deleted') page.close();
    else page.patch({ isActive: false });
  }

  function rowActions(row: StorageBinRow): DesktopAction[] {
    return [
      { label: 'Open', icon: 'details', shortcut: 'Enter', onSelect: () => show(row), allowed: true },
      { label: 'Edit', icon: 'edit', onSelect: () => { setSelectedKey(row.id); setForm('edit'); }, allowed: can('inv.store.update') },
      row.isActive
        ? { label: 'Delete', icon: 'retire', onSelect: () => { setSelectedKey(row.id); confirmRetire(row); }, allowed: can('inv.store.delete'), danger: true, separatorBefore: true }
        : { label: 'Reactivate', icon: 'reactivate', onSelect: () => { setSelectedKey(row.id); reactivate(row); }, allowed: can('inv.store.update'), separatorBefore: true },
    ];
  }

  const toolbarActions: DesktopAction[] = [
    { label: 'Add bin', icon: 'add', primary: true, onSelect: () => setForm('create'), allowed: can('inv.store.create') },
  ];

  const columns: Array<GridColumn<StorageBinRow>> = [
    { key: 'code', header: 'Code', width: 100, render: (r) => <span className="mono">{r.code}</span> },
    { key: 'name', header: 'Bin', width: 200, render: (r) => <span className="withicon"><AppIcon kind="bin" />{r.nameEn}</span> },
    { key: 'store', header: 'Store', width: 200, render: (r) => <span className="dim">{r.storeCode} — {r.storeName}</span> },
    { key: 'farm', header: 'Farm', width: 120, render: (r) => <span className="dim">{r.farmCode ?? 'Main store'}</span> },
    { key: 'items', header: 'Items in it', align: 'end', width: 100, render: (r) => r.itemsInStock },
    {
      key: 'status',
      header: 'Status',
      width: 110,
      render: (r) => (r.isActive ? <span className="pill">Active</span> : <span className="pill pill--alert">⚠ Deleted</span>),
    },
  ];

  // Edit opens over the list or over the entry's own screen.
  const formModal = form && (
    <Modal title={form === 'edit' && selected ? `Edit ${selected.code}` : 'Add a storage bin'} onClose={() => setForm(null)}>
      <BinForm
        key={form === 'edit' && selected ? selected.id : 'create'}
        initial={form === 'edit' ? (selected ?? undefined) : undefined}
        storeId={form === 'create' && storeId ? storeId : undefined}
        onCancel={() => setForm(null)}
        onSaved={(message) => { invalidate(); setNotice({ tone: 'ok', message }); setForm(null); }}
      />
    </Modal>
  );

  const openRow = page.row;
  if (openRow) {
    return (
      <>
        <RecordPage
          backLabel="Back to storage bins"
          onBack={() => show(null)}
          icon={<AppIcon kind="bin" size={18} />}
          code={openRow.code}
          name={openRow.nameEn}
          nameUr={openRow.nameUr}
          status={<span className={openRow.isActive ? 'pill' : 'pill pill--alert'}>{openRow.isActive ? 'Active' : '⚠ Deleted'}</span>}
          actions={rowActions(openRow)}
          notice={notice}
        >
          <BinPage row={openRow} />
        </RecordPage>
        {formModal}
      </>
    );
  }

  return (
    <div className="workbench__main">
      <div className="filterbar">
        <SearchBar value={search} onChange={setSearch} placeholder="Search bins by code, name or store…" />
        <StorePicker label="Store" value={storeId} onChange={setStoreId} placeholder="All stores" clearable stores={stores} />
        <label className="settings__toggle">
          <input type="checkbox" checked={showRetired} onChange={(e) => setShowRetired(e.target.checked)} />
          <span className="field__label">Show deleted</span>
        </label>
      </div>

      <ActionBar actions={toolbarActions} />

      {isError && <ErrorNote message={error.message} />}
      {notice && (notice.tone === 'ok' ? <OkNote message={notice.message} /> : <ErrorNote message={notice.message} />)}

      <DataGrid
        id="reference-bins"
        label="Storage bins"
        columns={columns}
        rows={rows}
        rowKey={(r) => r.id}
        selectedKey={selectedKey}
        onSelect={setSelectedKey}
        // One tap opens it on its own screen.
        onOpen={show}
        openOnClick
        actions={rowActions}
        loading={isPending}
        empty={search ? 'No bins match your search.' : `No storage bins yet. ${OPTIONAL_NOTE}`}
      />

      {formModal}
    </div>
  );
}

/** A store's bins, as a tab in the Stores panel's detail pane. */
export function StoreBinsTab({ store }: { store: StoreRow }) {
  const { can } = useAuth();
  const [form, setForm] = useState<{ mode: 'create' } | { mode: 'edit'; row: StorageBinRow } | null>(null);
  const [notice, setNotice] = useState<{ tone: 'ok' | 'error'; message: string } | null>(null);

  const { data, isPending, isError, error } = useQuery({
    queryKey: ['storage-bins', 'store-tab', store.id],
    queryFn: () => api.get('/api/v1/storage-bins', { storeId: store.id, includeInactive: 'true' }),
  });
  const { invalidate, confirmRetire, reactivate } = useBinLifecycle(setNotice);

  if (isPending) return <LoadingNote what="Loading bins…" />;
  if (isError) return <ErrorNote message={error.message} />;

  return (
    <div className="stack">
      {notice && (notice.tone === 'ok' ? <OkNote message={notice.message} /> : <ErrorNote message={notice.message} />)}
      {can('inv.store.create') && store.isActive && (
        <div>
          <button type="button" className="btn btn--sm btn--withicon" onClick={() => setForm({ mode: 'create' })}>
            <AppIcon kind="add" size={14} />
            Add bin
          </button>
        </div>
      )}
      <Table
        count={data.length}
        empty={`${store.code} has no bins. ${OPTIONAL_NOTE}`}
        head={
          <tr>
            <th>Code</th>
            <th>Bin</th>
            <th className="num">Items in it</th>
            <th>Status</th>
            <th aria-label="Actions" />
          </tr>
        }
      >
        {data.map((b) => (
          <tr key={b.id}>
            <td className="mono">{b.code}</td>
            <td>{b.nameEn}</td>
            <td className="num">{b.itemsInStock}</td>
            <td>{b.isActive ? <span className="pill">Active</span> : <span className="pill pill--alert">⚠ Deleted</span>}</td>
            <td>
              {can('inv.store.update') && (
                <button type="button" className="btn btn--ghost btn--sm" onClick={() => setForm({ mode: 'edit', row: b })}>
                  Edit
                </button>
              )}
              {b.isActive
                ? can('inv.store.delete') && (
                    <button type="button" className="btn btn--ghost btn--sm" onClick={() => confirmRetire(b)}>
                      Delete
                    </button>
                  )
                : can('inv.store.update') && (
                    <button type="button" className="btn btn--ghost btn--sm" onClick={() => reactivate(b)}>
                      Reactivate
                    </button>
                  )}
            </td>
          </tr>
        ))}
      </Table>

      {form && (
        <Modal title={form.mode === 'edit' ? `Edit ${form.row.code}` : `Add a bin to ${store.code}`} onClose={() => setForm(null)}>
          <BinForm
            key={form.mode === 'edit' ? form.row.id : 'create'}
            initial={form.mode === 'edit' ? form.row : undefined}
            storeId={store.id}
            onCancel={() => setForm(null)}
            onSaved={(message) => { invalidate(); setNotice({ tone: 'ok', message }); setForm(null); }}
          />
        </Modal>
      )}
    </div>
  );
}
